import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // user: undefined = loading, null = not authed, object = authed
  const [user, setUser] = useState(undefined);

  const checkAuth = useCallback(async () => {
    // If returning from Emergent OAuth callback, skip /me — AuthCallback will set the session first
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setUser(null);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // Unauthenticated is the normal case here (no cookie yet) — log only
      // unexpected non-401 errors to console for diagnostics.
      if (err?.response && err.response.status !== 401) {
        // eslint-disable-next-line no-console
        console.warn("[auth] /me check failed:", err.response.status, err.message);
      }
      setUser(null);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Network/server hiccup shouldn't block local logout — but surface it.
      // eslint-disable-next-line no-console
      console.warn("[auth] logout request failed; clearing client state anyway:", err?.message || err);
    }
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      user,
      loading: user === undefined,
      login,
      register,
      logout,
      refreshUser,
    }),
    [user, login, register, logout, refreshUser],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
